import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { OAUTH_MESSAGE_TYPE, OAUTH_STORAGE_KEY } from '../utils/oauthRelay';

// 首次/再次登录后同步 profile 字段。幂等：保留首次 auth_provider，扩展 linked_providers / google_sub。
async function syncProfileFromUser(user) {
  if (!supabase || !user) return;

  // app_metadata.providers 是所有已链接的 provider 列表（数组）；
  // app_metadata.provider 只是首次注册的 provider（单值），不反映 OAuth 链接情况。
  const providers = Array.isArray(user.app_metadata?.providers)
    ? user.app_metadata.providers
    : [user.app_metadata?.provider].filter(Boolean);

  // 从 identities 数组里抽 google sub（最权威，不被 user_metadata 覆盖影响）
  const identities = Array.isArray(user.identities) ? user.identities : [];
  const googleIdentity = identities.find(i => i.provider === 'google');
  const googleSub = googleIdentity?.identity_data?.sub || googleIdentity?.id || null;

  const meta = user.user_metadata || {};
  const displayName = meta.full_name || meta.name || null;

  const { data: existing } = await supabase
    .from('profiles')
    .select('id, auth_provider, google_sub, linked_providers, display_name')
    .eq('id', user.id)
    .maybeSingle();

  const linked = new Set(existing?.linked_providers || []);
  providers.forEach(p => linked.add(p));

  const next = {
    id: user.id,
    display_name: existing?.display_name || displayName,
    auth_provider: existing?.auth_provider || providers[0] || 'email',
    google_sub: googleSub || existing?.google_sub || null,
    linked_providers: Array.from(linked),
  };

  await supabase.from('profiles').upsert(next, { onConflict: 'id' });
}

// OAUTH_MESSAGE_TYPE / OAUTH_STORAGE_KEY 见 utils/oauthRelay.js：postMessage 依赖
// window.opener 这条引用，但 Google 账号选择页（accounts.google.com）会带
// Cross-Origin-Opener-Policy: same-origin 响应头——弹窗一旦跳过去，浏览器会把 opener
// 引用永久切断（哪怕之后又跳回同源页面也恢复不了），postMessage 可能压根送不到主窗口。
// localStorage 是同源存储，不依赖窗口间的脚本引用，弹窗侧写入后，主窗口用 storage 事件
// + 轮询兜底读取，postMessage 仍保留作为不受影响时的快速通道。

// signInWithGoogle 内部错误码（非 Supabase 原生报错）翻成用户能看懂的话；
// 其余 Supabase 原生报错原样透传（技术性但至少提供真实信息）。
// 两处调用方共用同一份翻译：AuthModal 的内联错误 + App.jsx 顶层重试提示条。
export function describeAuthError(message, lang) {
  if (message === 'popup_blocked') {
    return lang === 'en'
      ? 'Popup blocked — please allow popups for this site and try again'
      : '弹窗被拦截了 — 请允许本站弹窗后重试';
  }
  if (message === 'no_auth_url' || message === 'oauth_no_code') {
    return lang === 'en' ? 'Sign-in did not complete, please retry' : '登录未完成，请重试';
  }
  return message;
}

export function useAuth() {
  const [user, setUser] = useState(null);
  // 管理后台接口鉴权需要真实 access token（2026-07-23 修复：之前只信一个能在
  // 公开 JS 包里扒到的 ?uid= 参数），只存 user 不够，得把 session 本身也存下来。
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // OAuth 失败时（如 PKCE code verifier 丢失）此前完全静默——用户只看到"还是未登录"，
  // 没有任何提示。现在把失败原因暴露出去，让上层 UI 能提示用户重试登录。
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) console.warn(`[auth] getSession 错误:`, error.message);
      console.log(`[auth] 初始 getSession: loggedIn=${!!session?.user}`);
      setUser(session?.user ?? null);
      setSession(session ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`[auth] event=${event} loggedIn=${!!session?.user}`);
      setUser(session?.user ?? null);
      setSession(session ?? null);
      if (event === 'SIGNED_IN' && session?.user) {
        syncProfileFromUser(session.user).catch(err => console.warn(`[auth] syncProfile failed:`, err?.message));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithEmail = async (email, password) => {
    if (!supabase) return { error: new Error('Supabase not configured') };
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUpWithEmail = async (email, password) => {
    if (!supabase) return { error: new Error('Supabase not configured') };
    const { error } = await supabase.auth.signUp({ email, password });
    return { error };
  };

  // 弹窗式 OAuth，见风险清单 §7.18：整页跳转（window.location.href 出去再跳回来）
  // 会让主页面被卸载重载，PKCE code verifier 存活与否要靠这次"消失又回来"扛住 Chrome
  // 反追踪清理机制，撞上就丢。改成弹窗后主页面全程不卸载、不导航——verifier 从生成
  // 到最终 exchange 全程待在同一个从未离开过的页面里，弹窗只负责走一趟 Google 流程，
  // 把最终拿到的 code 用 postMessage 传回来，exchange 仍在主页面这边完成。
  // 弹窗侧的接收/postMessage/关闭逻辑见 src/index.js（弹窗加载到 redirectTo 时执行）。
  // 注意：这里不写 authError（全局提示条状态）——signInWithGoogle 同时被 AuthModal
  // （modal 开着，能就地展示具体错误）和 App.jsx 顶层"重试登录"按钮（modal 早关了，
  // 只能靠全局提示条）两处调用。若在这里统一写 authError，modal 打开时点击登录失败
  // 会同时冒出 modal 内联错误 + 顶层全局提示条两条不一致的提示（已实测复现过）。
  // 全局提示条只应该由"重试登录"按钮自己失败时触发，交给调用方决定要不要展示。
  const signInWithGoogle = () => new Promise((resolve) => {
    if (!supabase) { resolve({ error: new Error('Supabase not configured') }); return; }
    try { localStorage.removeItem(OAUTH_STORAGE_KEY); } catch {}

    (async () => {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}${window.location.pathname}`,
          queryParams: { access_type: 'offline', prompt: 'select_account' },
          skipBrowserRedirect: true,
        },
      });
      if (error || !data?.url) { resolve({ error: error || new Error('no_auth_url') }); return; }

      const popup = window.open(
        data.url, 'primarylens_oauth',
        'width=480,height=640,menubar=no,toolbar=no,location=no,status=no'
      );
      if (!popup) {
        resolve({ error: new Error('popup_blocked') });
        return;
      }

      let settled = false;
      const cleanup = () => {
        window.removeEventListener('message', onMessage);
        window.removeEventListener('storage', onStorage);
        clearInterval(poller);
        clearTimeout(giveUpTimer);
        try { localStorage.removeItem(OAUTH_STORAGE_KEY); } catch {}
      };
      // code/error 只应该被处理一次，不管是从 postMessage、storage 事件还是轮询哪条通道先到
      const finish = async (code, relayError) => {
        if (settled) return;
        settled = true;
        cleanup();
        try { popup.close(); } catch {}
        if (!code) {
          resolve({ error: new Error(relayError || 'oauth_no_code') });
          return;
        }
        const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
        if (exchangeError) {
          console.error('[auth] 弹窗回传 code 后 exchange 失败:', exchangeError.message);
        }
        resolve({ error: exchangeError || null });
      };
      const onMessage = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== OAUTH_MESSAGE_TYPE) return;
        finish(event.data.code, event.data.error);
      };
      window.addEventListener('message', onMessage);

      // storage 事件：另一个同源窗口写 localStorage 时才会触发（写入方自己的窗口不会收到），
      // 不依赖 window.opener 引用，COOP 把 opener 切断后这条仍然有效
      const onStorage = (event) => {
        if (event.key !== OAUTH_STORAGE_KEY || !event.newValue) return;
        try {
          const payload = JSON.parse(event.newValue);
          finish(payload.code, payload.error);
        } catch {}
      };
      window.addEventListener('storage', onStorage);

      // 轮询兜底（storage 事件个别浏览器/场景下可能不触发）+ 用户手动关掉弹窗检测。
      // 2026-07-06 实测：accounts.google.com 的 COOP 隔离了浏览上下文后，Chrome 会警告
      // "Cross-Origin-Opener-Policy policy would block the window.closed call"——跨域
      // 窗口引用的属性读取本身也可能被拦，popup.closed 不再保证可靠，包一层 try/catch，
      // 读不到就当作"还没关"，靠下面的 giveUpTimer 兜底避免永远悬着。
      const poller = setInterval(() => {
        if (settled) return;
        let raw = null;
        try { raw = localStorage.getItem(OAUTH_STORAGE_KEY); } catch {}
        if (raw) {
          try {
            const payload = JSON.parse(raw);
            finish(payload.code, payload.error);
            return;
          } catch {}
        }
        // 用户手动关掉弹窗（未走完流程，也没有 relay 数据）：静默结束，不算错误、不弹提示条
        let closed = false;
        try { closed = popup.closed; } catch {}
        if (closed) {
          settled = true;
          cleanup();
          resolve({ error: null, cancelled: true });
        }
      }, 500);

      // 兜底超时：popup.closed 万一被 COOP 拦到读不出真实值，上面那条检测可能永远不触发，
      // 5 分钟还没等到任何结果就当作用户放弃了，静默结束（不算错误，不弹提示条）
      const giveUpTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        try { popup.close(); } catch {}
        resolve({ error: null, cancelled: true });
      }, 5 * 60 * 1000);
    })();
  });

  const signOut = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
  };

  return {
    user, session, loading, signInWithEmail, signUpWithEmail, signInWithGoogle, signOut,
    authError, dismissAuthError: () => setAuthError(null), reportAuthError: setAuthError,
  };
}
